import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Eye, EyeOff, Send } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SAMPLE = { huurder: "Jan de Vries", maand: "juni 2026", bedrag: "€ 1.850,00", iban: "NL00 BANK 0123 4567 89", boete_per_dag: "€ 25,00" };
const fill = (txt) => (txt || "")
  .replaceAll("{huurder}", SAMPLE.huurder)
  .replaceAll("{maand}", SAMPLE.maand)
  .replaceAll("{bedrag}", SAMPLE.bedrag)
  .replaceAll("{iban}", SAMPLE.iban)
  .replaceAll("{boete_per_dag}", SAMPLE.boete_per_dag);

const offsetLabel = (o) => Number(o) < 0 ? `${Math.abs(Number(o))} dagen vóór verval` : Number(o) === 0 ? "op de vervaldag" : `${o} dagen ná verval`;

function MailPreview({ subject, body }) {
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white" data-testid="reminder-preview">
      <div className="bg-slate-800 px-4 py-3 flex items-center gap-2">
        <span className="h-7 w-7 rounded bg-white p-0.5 flex items-center justify-center"><img src="/logo.png" alt="" className="h-full w-full object-contain" /></span>
        <span className="text-white text-sm font-bold">Praktisch-Debiteurenbeheer</span>
      </div>
      <div className="p-4 text-sm text-slate-700">
        <div className="text-xs text-slate-400 mb-1">Onderwerp</div>
        <div className="font-semibold text-slate-900 mb-3">{fill(subject) || "—"}</div>
        <div className="whitespace-pre-wrap leading-relaxed">{fill(body) || "—"}</div>
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-500 mb-1">Openstaande termijnen</div>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-slate-100"><th className="p-1.5 text-left">Periode</th><th className="p-1.5 text-left">Vervaldatum</th><th className="p-1.5 text-left">Te betalen</th></tr></thead>
            <tbody><tr><td className="p-1.5 border-t">juni 2026</td><td className="p-1.5 border-t">01-06-2026</td><td className="p-1.5 border-t font-semibold">€ 1.850,00</td></tr></tbody>
          </table>
          <div className="mt-2 text-sm font-semibold">Totaal openstaand: € 1.850,00</div>
          <div className="mt-3"><span className="inline-block bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Betaal nu met iDEAL</span></div>
        </div>
      </div>
    </div>
  );
}

export default function ReminderStagesEditor({ stages, setStages }) {
  const [preview, setPreview] = useState({});
  const [testing, setTesting] = useState(null);
  const setStage = (i, k, v) => setStages(stages.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const addStage = () => setStages([...stages, { offset: 0, type: `fase_${stages.length + 1}`, tone: "Zakelijk", subject: "Betalingsherinnering", body: "Geachte {huurder},\n\nDe termijn van {maand} ({bedrag}) staat open. Wij verzoeken u vriendelijk te betalen.", enabled: true }]);
  const removeStage = (i) => setStages(stages.filter((_, idx) => idx !== i));
  const sendTest = async (i, s) => {
    setTesting(i);
    try {
      const r = await api.post("/settings/reminder-stages/test", { stage: s });
      toast.success(`Testmail verstuurd naar ${(r.data.to || []).join(", ")}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Testmail mislukt"); }
    finally { setTesting(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={addStage} data-testid="reminder-add-stage"><Plus className="h-4 w-4 mr-2" /> Fase toevoegen</Button>
      </div>
      {stages.length === 0 && <p className="text-sm text-slate-400">Nog geen fasen. Voeg er een toe.</p>}
      {stages.map((s, i) => (
        <div key={i} className="rounded-lg border p-4 space-y-3" data-testid={`reminder-stage-${i}`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs">Dagen t.o.v. verval</Label>
              <Input type="number" value={s.offset} onChange={(e) => setStage(i, "offset", e.target.value)} data-testid={`reminder-offset-${i}`} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Onderwerp</Label>
              <Input value={s.subject} onChange={(e) => setStage(i, "subject", e.target.value)} data-testid={`reminder-subject-${i}`} />
            </div>
            <label className="flex items-center gap-2 text-sm pb-2">
              <input type="checkbox" checked={s.enabled !== false} onChange={(e) => setStage(i, "enabled", e.target.checked)} data-testid={`reminder-enabled-${i}`} /> Actief
            </label>
          </div>
          <div>
            <Label className="text-xs">Tekst</Label>
            <Textarea rows={4} value={s.body} onChange={(e) => setStage(i, "body", e.target.value)} className="text-sm" data-testid={`reminder-body-${i}`} />
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-slate-400">{offsetLabel(s.offset)}</span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => sendTest(i, s)} disabled={testing === i} data-testid={`reminder-test-${i}`}><Send className="h-4 w-4 mr-1" /> {testing === i ? "Versturen…" : "Testmail"}</Button>
              <Button variant="ghost" size="sm" onClick={() => setPreview((p) => ({ ...p, [i]: !p[i] }))} data-testid={`reminder-preview-toggle-${i}`}>
                {preview[i] ? <><EyeOff className="h-4 w-4 mr-1" /> Verberg voorbeeld</> : <><Eye className="h-4 w-4 mr-1" /> Toon voorbeeld</>}
              </Button>
              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeStage(i)} data-testid={`reminder-remove-${i}`}><Trash2 className="h-4 w-4 mr-1" /> Verwijderen</Button>
            </div>
          </div>
          {preview[i] && <MailPreview subject={s.subject} body={s.body} />}
        </div>
      ))}
    </div>
  );
}
